import React, { FC } from "react";
import { useNavigate } from "react-router-dom";

import { Stack } from "@mui/material";

import { WelcomeLogo } from "@assets/svg";

import { useIsDesktop } from "@hooks/layout";

import { MainTextTypography } from "@components/RecoveryDialog/components";
import { ERoutes } from "@utils/constants";
import styled from "styled-components";

import { colors } from "@/styles/design-system";

type TWelcome = {
  handleClose: () => void;
};

/* Each option is a self-contained block: icon + heading, a description, and a
   clear CTA button — so it reads as a distinct choice with an obvious action
   (the old icon-row tiles looked like neither a button nor a selection). */
const OptionBlock = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 18px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(235, 194, 142, 0.18);
  display: flex;
  flex-direction: column;
`;

/* The two option blocks sit side-by-side (equal-height columns) when there's
   room, and collapse to a single stacked column once the modal gets narrow
   (phones). They carry little text, so two columns read fine on desktop. */
const OptionsRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: stretch;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const OptionTitle = styled.h3`
  margin: 0 0 6px;
  font-family: "Raleway", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #fbf4ec;
  line-height: 1.3;
  text-align: center;
`;

const OptionDesc = styled.p`
  margin: 0 0 20px;
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  color: #e0d2c1;
  line-height: 1.45;
  text-align: center;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  margin-top: auto; /* pin to the bottom so it aligns with the sibling box's button even when one heading wraps to 2 lines */
  padding: 13px 18px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  color: ${colors.brown.darkest};
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 0 24px rgba(186, 144, 92, 0.25);
  transition: box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (hover: hover) {
    &:hover { box-shadow: 0 4px 32px rgba(186, 144, 92, 0.45); }
  }
`;

const SecondaryBtn = styled.button`
  width: 100%;
  margin-top: auto; /* pin to the bottom so it aligns with the sibling box's button even when one heading wraps to 2 lines */
  padding: 13px 18px;
  border-radius: 8px;
  background: transparent;
  border: 1.5px solid rgba(235, 194, 142, 0.4);
  color: #ebc28e;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (hover: hover) {
    &:hover { border-color: rgba(235, 194, 142, 0.65); background: rgba(235, 194, 142, 0.08); }
  }
`;

/* Blue button icon for the gold "Start Test" CTA (gold buttons carry blue
   icons site-wide — #60a3d7). Right-arrow = "start / go". */
const StartIconBlue = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* Blue music-note icon for the outline "Browse Music" button. It matched the
   button's gold text before, which left the two CTAs sitting side by side with
   differently-coloured glyphs; blue pairs it with Start Test so the row reads as
   one set of choices. */
const MusicIconBlue = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
  </svg>
);

const Welcome: FC<TWelcome> = ({ handleClose }) => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const goTo = (route: string) => {
    handleClose();
    navigate(route);
  };

  return (
    <Stack spacing={0} width="100%" alignItems="center" justifyContent="center">
      {/* Logo + heading. The spacing around the heading is set by the heading's
          own margins (8px above logo→heading, 12px below heading→options) so the
          values are exact regardless of the surrounding Stack gaps. */}
      <Stack spacing={0} width="100%" alignItems="center">
        <WelcomeLogo style={{ width: isDesktop ? "84px" : "64px", height: "auto" }} />
        <MainTextTypography textAlign="center" style={{ fontSize: "40px", lineHeight: 1.1, marginTop: "12px", marginBottom: "32px" }}>Welcome to Audio Sanctum</MainTextTypography>
      </Stack>

      <OptionsRow>
        {/* Primary path: the quick free-use questionnaire. Routes to the
            catalog with ?test=true, which auto-opens the question flow. */}
        <OptionBlock>
          <OptionTitle>Free, Subscription or License?</OptionTitle>
          <OptionDesc>Get an answer in less than 30s.</OptionDesc>
          <PrimaryBtn type="button" onClick={() => goTo(ERoutes.CatalogWithTest)}><StartIconBlue />Start Test</PrimaryBtn>
        </OptionBlock>

        <OptionBlock>
          <OptionTitle>Find your Music</OptionTitle>
          <OptionDesc>Start exploring our music library.</OptionDesc>
          <SecondaryBtn type="button" onClick={() => goTo(ERoutes.Catalog)}><MusicIconBlue />Browse Music</SecondaryBtn>
        </OptionBlock>
      </OptionsRow>
    </Stack>
  );
};

export default Welcome;
